import { cs } from '../../lib/currency';
/**
 * PR 3 Tenant-detail tab bodies. Each is a single-purpose component
 * that reads its own endpoint via the hooks in tenantDetailHooks.js.
 *
 * Kept in a single file to minimise import noise in the parent detail
 * page. If any tab grows beyond ~200 lines, split it out.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Loader2, AlertTriangle, CheckCircle2, Save, Mail, Building, Phone, Globe, MessageCircle, UserPlus, KeyRound, Ban, ShieldCheck, Zap } from "lucide-react";
import { toast } from "sonner";
import {
  saveTenantContacts,
  useTenantContacts,
  useTenantOverview,
  useTenantPlan,
  useTenantUsage,
} from "./tenantDetailHooks";
import { TenantStorageCard } from "./TenantStorageCard";

const _healthClass = {
  healthy:         "bg-emerald-100 text-emerald-700 border-emerald-200",
  warning:         "bg-amber-100 text-amber-800 border-amber-200",
  critical:        "bg-rose-100 text-rose-700 border-rose-200",
  not_configured:  "bg-slate-100 text-slate-600 border-slate-200",
};

const Card = ({ title, children, testid }) => (
  <div className="bg-white border border-slate-200 rounded-lg p-5" data-testid={testid}>
    {title ? <h3 className="font-semibold text-slate-800 mb-3">{title}</h3> : null}
    {children}
  </div>
);

const Row = ({ label, value, mono, tid }) => (
  <div className="flex justify-between border-b border-slate-100 py-2 text-sm gap-4">
    <span className="text-slate-500 flex-shrink-0">{label}</span>
    <span
      className={`font-medium text-slate-800 text-right truncate ${mono ? "font-mono text-xs" : ""}`}
      data-testid={tid}
      title={typeof value === "string" ? value : undefined}
    >
      {value || <span className="text-slate-400">—</span>}
    </span>
  </div>
);

const LoadingBlock = () => (
  <div className="flex items-center justify-center py-10 text-slate-500 gap-2">
    <Loader2 className="w-4 h-4 animate-spin" /> Loading…
  </div>
);

const ErrorBlock = ({ error }) => (
  <div className="p-4 border border-rose-200 bg-rose-50 rounded text-sm text-rose-700">
    Failed to load: {error}
  </div>
);

// ============================================================================
// Overview tab
// ============================================================================

// iter335h (#8/#9) — edit trial-end / plan-start / renewal + frequency.
// Lapsed trial with no renewal auto-marks the tenant inactive (nightly job
// + on-save recompute).
function SubscriptionDatesEditCard({ tenantId, sub }) {
  const [trialEnds, setTrialEnds] = useState((sub.trial_ends_at || "").slice(0, 10));
  const [planStart, setPlanStart] = useState((sub.plan_start_date || "").slice(0, 10));
  const [renewal, setRenewal] = useState((sub.current_period_ends_at || sub.current_period_end || "").slice(0, 10));
  const [cycle, setCycle] = useState(sub.billing_cycle || "monthly");
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await axios.patch(`${API}/super-admin/tenants/${tenantId}/subscription-dates`, {
        trial_ends_at: trialEnds || null,
        plan_start_date: planStart || null,
        current_period_end: renewal || null,
        billing_cycle: cycle,
      }, { headers: authHeader() });
      toast.success(`Saved — tenant is now ${data.subscription?.status}`);
      setTimeout(() => window.location.reload(), 900);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  const field = (label, val, setter, tid) => (
    <div>
      <label className="text-xs text-slate-500 block mb-1">{label}</label>
      <input type="date" value={val} onChange={(e) => setter(e.target.value)} data-testid={tid}
        className="w-full text-sm border border-slate-200 rounded px-2 py-1.5" />
    </div>
  );

  return (
    <Card title="Dates & Renewal" testid="ov-dates-edit">
      <div className="grid grid-cols-2 gap-3">
        {field("Trial ends", trialEnds, setTrialEnds, "sub-trial-ends")}
        {field("Plan start date", planStart, setPlanStart, "sub-plan-start")}
        {field("Renewal date", renewal, setRenewal, "sub-renewal")}
        <div>
          <label className="text-xs text-slate-500 block mb-1">Renewal frequency</label>
          <select value={cycle} onChange={(e) => setCycle(e.target.value)} data-testid="sub-cycle"
            className="w-full text-sm border border-slate-200 rounded px-2 py-1.5">
            <option value="monthly">Monthly</option>
            <option value="annual">Annual (2 mo free)</option>
          </select>
        </div>
      </div>
      <p className="text-[11px] text-slate-400 mt-2">
        If the trial ends with no renewal/plan-start date in the future, the tenant is marked inactive.
      </p>
      <button onClick={save} disabled={busy} data-testid="sub-dates-save"
        className="mt-3 inline-flex items-center gap-1.5 bg-slate-900 hover:bg-slate-800 disabled:opacity-50 text-white text-sm px-3 py-1.5 rounded">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
        Save dates
      </button>
    </Card>
  );
}


export function TenantOverviewTab({ tenantId }) {
  const { data, loading, error } = useTenantOverview(tenantId);

  if (loading) return <LoadingBlock />;
  if (error) return <ErrorBlock error={error} />;
  if (!data) return null;

  const tenant = data.tenant || {};
  const sub = data.subscription || {};
  const uc = data.user_counts || {};
  const health = data.health || {};
  const integ = data.integrations || {};
  const lastAction = data.last_super_admin_action;

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="tenant-tab-overview">
      <Card title="Identity" testid="ov-identity">
        <Row label="Tenant name" value={tenant.name} tid="ov-name" />
        <Row label="Tenant ID" value={tenant.id} mono tid="ov-id" />
        <Row label="Slug" value={tenant.slug} mono tid="ov-slug" />
        <Row label="Custom domain" value={tenant.custom_domain} mono tid="ov-domain" />
        <Row label="Canonical hostname" value={tenant.canonical_hostname} mono tid="ov-canonical" />
        <Row label="Business name" value={data.business_name} tid="ov-biz" />
        <Row label="Created" value={tenant.created_at?.slice(0, 10)} tid="ov-created" />
      </Card>

      <Card title="Subscription" testid="ov-subscription">
        <Row label="Status" value={sub.status} tid="ov-status" />
        <Row label="Plan" value={sub.plan_name || sub.plan_id} mono={!sub.plan_name} tid="ov-plan" />
        <Row label="Trial ends" value={sub.trial_ends_at?.slice(0, 10)} tid="ov-trial" />
        <Row label="Renews" value={sub.current_period_ends_at?.slice(0, 10)} tid="ov-renewal" />
        <Row label="Add-on features" value={`${(sub.addon_feature_keys || []).length}`} tid="ov-addons" />
      </Card>

      <SubscriptionDatesEditCard tenantId={tenantId} sub={sub} />

      <Card title="Users" testid="ov-users">
        <Row label="Total active" value={uc.active} tid="ov-users-active" />
        <Row label="Total" value={uc.total} tid="ov-users-total" />
        {Object.entries(uc.by_role || {}).map(([role, n]) => (
          <Row key={role} label={role.charAt(0).toUpperCase() + role.slice(1)} value={n} tid={`ov-role-${role}`} />
        ))}
      </Card>

      <Card title="Health" testid="ov-health">
        <div className="flex items-center gap-2 mb-3">
          <span
            className={`text-xs px-2 py-1 rounded border ${_healthClass[health.level] || _healthClass.not_configured}`}
            data-testid="ov-health-badge"
          >
            {health.level === "healthy" ? (
              <CheckCircle2 className="w-3 h-3 inline mr-1" />
            ) : (
              <AlertTriangle className="w-3 h-3 inline mr-1" />
            )}
            {(health.level || "unknown").replace(/_/g, " ")}
          </span>
          <span className="text-xs text-slate-500">
            {(health.reasons || []).length === 0 ? "no issues" : `${health.reasons.length} issue(s)`}
          </span>
        </div>
        {(health.reasons || []).length === 0 ? (
          <p className="text-xs text-slate-500">Everything looks good.</p>
        ) : (
          <ul className="text-xs text-slate-600 list-disc pl-4 space-y-1">
            {health.reasons.map((r) => <li key={r} className="font-mono">{r}</li>)}
          </ul>
        )}
      </Card>

      <Card title="Integrations" testid="ov-integrations">
        {Object.entries(integ).map(([name, cfg]) => (
          <div key={name} className="flex items-center justify-between border-b border-slate-100 py-2 text-sm">
            <span className="text-slate-700 capitalize">{name}</span>
            <span
              className={`text-xs px-2 py-0.5 rounded border ${cfg.configured ? "bg-emerald-100 text-emerald-700 border-emerald-200" : "bg-slate-100 text-slate-500 border-slate-200"}`}
              data-testid={`ov-int-${name}`}
            >
              {cfg.configured ? (cfg.mode ? `${cfg.mode} mode` : "configured") : "not configured"}
            </span>
          </div>
        ))}
      </Card>

      <Card title="Recent super-admin action" testid="ov-last-action">
        {lastAction ? (
          <>
            <Row label="Action" value={lastAction.action || lastAction.event_type} tid="ov-la-action" />
            <Row label="Actor" value={lastAction.actor_email} mono tid="ov-la-actor" />
            <Row label="When" value={lastAction.created_at?.slice(0, 19).replace("T", " ")} mono tid="ov-la-when" />
          </>
        ) : (
          <p className="text-xs text-slate-500">No super-admin actions logged for this tenant yet.</p>
        )}
      </Card>
    </div>
  );
}


// ============================================================================
// Contact tab (with validation + audit)
// ============================================================================

const CONTACT_GROUPS = [
  {
    title: "Business",
    icon: Building,
    fields: [
      ["business_name", "Business name"],
      ["legal_business_name", "Legal business name"],
      ["business_address", "Business address"],
      ["website_url", "Website URL"],
    ],
  },
  {
    title: "Phone & Messaging",
    icon: Phone,
    fields: [
      ["phone_number", "Phone number"],
      ["whatsapp_number", "WhatsApp number"],
    ],
  },
  {
    title: "Primary contact",
    icon: Mail,
    fields: [
      ["primary_contact_name", "Name"],
      ["primary_contact_email", "Email"],
    ],
  },
  {
    title: "Billing contact",
    icon: Mail,
    fields: [
      ["billing_contact_name", "Name"],
      ["billing_contact_email", "Email"],
    ],
  },
  {
    title: "Technical contact",
    icon: Mail,
    fields: [
      ["technical_contact_name", "Name"],
      ["technical_contact_email", "Email"],
    ],
  },
  {
    title: "Support",
    icon: MessageCircle,
    fields: [
      ["support_contact_name", "Support contact name"],
      ["support_email", "Support email"],
    ],
  },
];

export function TenantContactTab({ tenantId }) {
  const { data, loading, error, reload } = useTenantContacts(tenantId);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (data?.contacts) setForm(data.contacts);
  }, [data]);

  const onChange = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    setDirty(true);
  };

  const onSave = async () => {
    setSaving(true);
    try {
      await saveTenantContacts(tenantId, form);
      toast.success("Contacts saved & audited");
      setDirty(false);
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail?.[0]?.msg
        || e.response?.data?.detail
        || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <LoadingBlock />;
  if (error) return <ErrorBlock error={error} />;

  return (
    <div className="space-y-4" data-testid="tenant-tab-contact">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {CONTACT_GROUPS.map(({ title, icon: Icon, fields }) => (
          <Card key={title} title={<span className="inline-flex items-center gap-2"><Icon className="w-4 h-4 text-slate-500" />{title}</span>} testid={`contact-group-${title.toLowerCase().replace(/\s+/g, "-")}`}>
            {fields.map(([k, label]) => (
              <label key={k} className="block mb-2">
                <span className="text-xs text-slate-500">{label}</span>
                <input
                  type="text"
                  value={form[k] || ""}
                  onChange={(e) => onChange(k, e.target.value)}
                  className="mt-1 w-full border border-slate-200 rounded px-2 py-1.5 text-sm focus:outline-none focus:border-slate-400"
                  data-testid={`contact-input-${k}`}
                />
              </label>
            ))}
          </Card>
        ))}
      </div>
      <div className="sticky bottom-0 bg-white border-t border-slate-200 py-3 flex justify-end">
        <button
          type="button"
          onClick={onSave}
          disabled={!dirty || saving}
          data-testid="contact-save-btn"
          className="inline-flex items-center gap-2 bg-slate-900 text-white text-sm px-4 py-2 rounded hover:bg-slate-800 disabled:opacity-40"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          Save contacts
        </button>
      </div>
    </div>
  );
}


// ============================================================================
// Plan & Features tab
// ============================================================================

// iter335h (#15) — Super Admin manual plan edit. Reuses the same
// proration / annual rules as tenant self-service via the
// /super-admin/tenants/{id}/plan-change endpoints.
function SuperAdminPlanEditCard({ tenantId, currentPlanId, currentAddons, currentCycle }) {
  const [plans, setPlans] = useState([]);
  const [planId, setPlanId] = useState(currentPlanId || "");
  const [cycle, setCycle] = useState(currentCycle || "monthly");
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    axios.get(`${API}/super-admin/plans/detailed`, { headers: authHeader() })
      .then((r) => setPlans((r.data.plans || []).filter((p) => p.status !== "archived")))
      .catch(() => {});
  }, []);

  const dirty = planId !== currentPlanId || cycle !== (currentCycle || "monthly");

  useEffect(() => {
    if (!planId || !dirty) { setPreview(null); return; }
    let cancel = false;
    const t = setTimeout(() => {
      axios.post(`${API}/super-admin/tenants/${tenantId}/plan-change/preview`,
        { plan_id: planId, addon_feature_keys: currentAddons || [], billing_cycle: cycle },
        { headers: authHeader() })
        .then((r) => { if (!cancel) setPreview(r.data); })
        .catch(() => { if (!cancel) setPreview(null); });
    }, 250);
    return () => { cancel = true; clearTimeout(t); };
  }, [planId, cycle, dirty, tenantId, currentAddons]);

  const apply = async () => {
    if (!window.confirm(`Change this tenant to "${planId}" (${cycle})? This applies immediately${preview?.direction === "upgrade" ? " and charges the prorated/annual amount to their card on file" : ""}.`)) return;
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/super-admin/tenants/${tenantId}/plan-change`,
        { plan_id: planId, addon_feature_keys: currentAddons || [], billing_cycle: cycle },
        { headers: authHeader() });
      const chg = data.charge_amount > 0
        ? (data.charge?.ok ? ` — charged ${cs()}${data.charge_amount}` : ` — ${cs()}${data.charge_amount} could not be charged (recorded as owed)`)
        : "";
      toast.success(`Plan changed to ${data.new_total != null ? cs() + data.new_total + "/mo" : planId}${chg}`);
      setTimeout(() => window.location.reload(), 900);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Plan change failed");
    } finally { setBusy(false); }
  };

  return (
    <Card title="Change Plan (manual)" testid="sa-plan-edit-card">
      <div className="space-y-3">
        <div>
          <label className="text-xs text-slate-500 block mb-1">Plan</label>
          <select value={planId} onChange={(e) => setPlanId(e.target.value)} data-testid="sa-plan-select"
            className="w-full text-sm border border-slate-200 rounded px-2 py-1.5">
            {plans.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name} {p.price_per_month != null ? `— ${cs()}${p.price_per_month}/mo` : "— Custom"}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="text-xs text-slate-500 block mb-1">Billing frequency</label>
          <div className="inline-flex rounded border border-slate-200 p-0.5">
            {[["monthly", "Monthly"], ["annual", "Annual (2 mo free)"]].map(([v, l]) => (
              <button key={v} data-testid={`sa-cycle-${v}`} onClick={() => setCycle(v)}
                className={`px-2.5 py-1 text-xs rounded ${cycle === v ? "bg-slate-900 text-white" : "text-slate-500"}`}>
                {l}
              </button>
            ))}
          </div>
        </div>
        {dirty && preview && (
          <div className="text-xs bg-slate-50 border border-slate-200 rounded p-2 space-y-1" data-testid="sa-plan-preview">
            <p className="font-medium capitalize">{preview.direction}</p>
            <p>Monthly: {cs()}{Number(preview.current_total).toFixed(2)} → <b>{cs()}{Number(preview.new_total).toFixed(2)}</b></p>
            {cycle === "annual" && <p>Billed annually: <b>{cs()}{Number(preview.annual_total).toFixed(2)}/yr</b> (save {cs()}{Number(preview.annual_savings).toFixed(2)})</p>}
            {cycle === "monthly" && preview.direction === "upgrade" && preview.proration_amount > 0 && (
              <p>Prorated charge now: <b>{cs()}{Number(preview.proration_amount).toFixed(2)}</b></p>
            )}
          </div>
        )}
        <button onClick={apply} disabled={!dirty || busy} data-testid="sa-plan-apply"
          className="inline-flex items-center gap-1.5 bg-slate-900 hover:bg-slate-800 disabled:opacity-50 text-white text-sm px-3 py-1.5 rounded">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          Apply change
        </button>
      </div>
    </Card>
  );
}

export function TenantPlanTab({ tenantId }) {
  const { data, loading, error } = useTenantPlan(tenantId);
  if (loading) return <LoadingBlock />;
  if (error) return <ErrorBlock error={error} />;
  if (!data) return null;

  const plan = data.plan || {};
  const sub = data.subscription || {};
  const pf = data.plan_features || {};
  const af = data.addon_features || {};

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="tenant-tab-plan">
      <Card title="Subscription" testid="plan-sub-card">
        <Row label="Status" value={sub.status} tid="plan-status" />
        <Row label="Plan" value={plan.name || sub.plan_id} tid="plan-name" />
        <Row label="Plan status" value={plan.status} tid="plan-plan-status" />
        <Row label="Monthly price" value={plan.monthly_price != null ? `${cs()}${plan.monthly_price}` : null} tid="plan-monthly" />
        <Row label="Annual price" value={plan.annual_price != null ? `${cs()}${plan.annual_price}` : null} tid="plan-annual" />
        <Row label="Trial start" value={sub.trial_start_at?.slice(0, 10)} tid="plan-trial-start" />
        <Row label="Trial ends" value={sub.trial_ends_at?.slice(0, 10)} tid="plan-trial-end" />
        <Row label="Renewal date" value={sub.current_period_ends_at?.slice(0, 10)} tid="plan-renewal" />
        {sub.pending_change && (
          <div className="mt-3 bg-amber-50 border border-amber-200 rounded p-2 text-xs text-amber-800">
            Pending plan change: {JSON.stringify(sub.pending_change)}
          </div>
        )}
      </Card>

      <Card title="Limits" testid="plan-limits-card">
        {Object.keys(plan.limits || {}).length === 0 ? (
          <p className="text-xs text-slate-500">No hard limits set on this plan.</p>
        ) : (
          Object.entries(plan.limits).map(([k, v]) => (
            <Row key={k} label={k} value={String(v)} tid={`plan-limit-${k}`} />
          ))
        )}
      </Card>

      <Card title="Features" testid="plan-features-card">
        <PlanFeatureList title="Direct" keys={pf.direct} tone="bg-emerald-50 text-emerald-700 border-emerald-200" testid="plan-features-direct" />
        <PlanFeatureList title="Add-on" keys={af.direct} tone="bg-sky-50 text-sky-700 border-sky-200" testid="plan-features-addon" />
        <PlanFeatureList title="Dependency-inherited" keys={pf.inherited} tone="bg-slate-50 text-slate-600 border-slate-200" testid="plan-features-inherited" />
        <PlanFeatureList title="Always-on" keys={data.always_on_features} tone="bg-violet-50 text-violet-700 border-violet-200" testid="plan-features-always-on" />
        {(pf.missing || []).length > 0 && (
          <PlanFeatureList
            title="Missing (from catalogue)"
            keys={pf.missing}
            tone="bg-rose-50 text-rose-700 border-rose-200"
            testid="plan-features-missing"
          />
        )}
      </Card>

      <SuperAdminPlanEditCard
        tenantId={tenantId}
        currentPlanId={sub.plan_id}
        currentAddons={sub.addon_feature_keys || af.direct || []}
        currentCycle={sub.billing_cycle || "monthly"}
      />
    </div>
  );
}

function PlanFeatureList({ title, keys, tone, testid }) {
  return (
    <div className="mb-3" data-testid={testid}>
      <p className="text-xs font-semibold text-slate-500 uppercase mb-1">{title} ({(keys || []).length})</p>
      <div className="flex flex-wrap gap-1">
        {(keys || []).length === 0 ? (
          <span className="text-xs text-slate-400">none</span>
        ) : (
          (keys || []).map((k) => (
            <span key={k} className={`text-[10px] font-mono px-1.5 py-0.5 rounded border ${tone}`}>
              {k}
            </span>
          ))
        )}
      </div>
    </div>
  );
}


// ============================================================================
// Usage & Limits tab
// ============================================================================

export function TenantUsageTab({ tenantId }) {
  const { data, loading, error } = useTenantUsage(tenantId);
  if (loading) return <LoadingBlock />;
  if (error) return <ErrorBlock error={error} />;
  if (!data) return null;

  const c = data.counters || {};
  const roles = c.users_by_role || {};

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="tenant-tab-usage">
      <Card title="Users by role" testid="usage-users-card">
        <Row label="Total users" value={c.users} tid="usage-users-total" />
        {Object.entries(roles).map(([role, n]) => (
          <Row key={role} label={role} value={n} tid={`usage-role-${role}`} />
        ))}
      </Card>

      <Card title="Programs & enrollments" testid="usage-programs-card">
        <Row label="Players" value={c.players} tid="usage-players" />
        <Row label="Programs" value={c.programs} tid="usage-programs" />
        <Row label="Classes" value={c.classes} tid="usage-classes" />
        <Row label="Enrollments (all)" value={c.enrollments} tid="usage-enrollments-all" />
        <Row label="Enrollments (active)" value={c.active_enrollments} tid="usage-enrollments-active" />
      </Card>

      <Card title="Facilities" testid="usage-facilities-card">
        <Row label="Lanes" value={c.lanes} tid="usage-lanes" />
        <Row label="Lane bookings" value={c.lane_bookings} tid="usage-lane-bookings" />
        <Row label="Tournaments" value={c.tournaments} tid="usage-tournaments" />
        <Row label="Blog posts" value={c.blog_posts} tid="usage-blog-posts" />
        <Row label="Monthly invoices" value={c.monthly_invoices} tid="usage-monthly-invoices" />
      </Card>

      <Card title="Limits vs usage" testid="usage-limits-card">
        {(data.against_limits || []).length === 0 ? (
          <p className="text-xs text-slate-500">No plan limits configured for comparison.</p>
        ) : (
          <div className="space-y-2">
            {data.against_limits.map((row) => (
              <div key={row.resource} className="flex items-center justify-between text-sm border-b border-slate-100 py-2">
                <span className="text-slate-500">{row.resource}</span>
                <span className={`font-medium ${row.over_limit ? "text-rose-700" : "text-slate-800"}`}>
                  {row.used ?? "—"} / {row.limit ?? "—"}
                </span>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}


// ============================================================================
// Users tab — merged from SuperAdminTenantUsers (PR 4)
// ============================================================================

const API = (typeof window !== "undefined" && window.location
  ? window.location.origin
  : process.env.REACT_APP_BACKEND_URL) + "/api";

const authHeader = () => {
  const t = localStorage.getItem("token") || localStorage.getItem("access_token");
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const ROLES = ["admin", "staff", "coach"];

export function TenantUsersTab({ tenantId }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [roleFilter, setRoleFilter] = useState("all");
  const [activeFilter, setActiveFilter] = useState("all");
  const [showCreate, setShowCreate] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const params = new URLSearchParams({ tenant_id: tenantId });
      if (roleFilter !== "all") params.set("role", roleFilter);
      const res = await axios.get(`${API}/super-admin/users?${params}`, { headers: authHeader() });
      setUsers(res.data.users || []);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally { setLoading(false); }
  }, [tenantId, roleFilter]);
  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() =>
    users.filter(u => activeFilter === "all"
      ? true
      : activeFilter === "active" ? u.is_active !== false : u.is_active === false)
  , [users, activeFilter]);

  const patch = async (userId, body, msg) => {
    try {
      await axios.patch(`${API}/super-admin/users/${userId}`, body, { headers: authHeader() });
      toast.success(msg);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Update failed");
    }
  };

  const resetPassword = async (u) => {
    if (!window.confirm(`Reset password for ${u.email}? A temporary password will be generated.`)) return;
    try {
      const res = await axios.post(`${API}/super-admin/users/${u.id}/reset-password`,
        { force_change: true }, { headers: authHeader() });
      const temp = res.data?.temp_password;
      if (temp) {
        window.prompt("Temporary password (copy now — shown once):", temp);
      }
      const es = res.data?.email_sent;
      if (es && es.ok) {
        toast.success(`Password reset — new temp password emailed to ${u.email}. Also shown once above.`);
      } else if (es && es.reason === "resend_not_configured") {
        toast.warning("Password reset — email NOT sent (platform Resend key not configured). Copy the temp password shown above.", { duration: 10000 });
      } else {
        toast.success("Password reset — temp password shown once above.");
      }
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Reset failed");
    }
  };

  return (
    <div className="space-y-4" data-testid="tenant-tab-users">
      <div className="flex items-center gap-3 flex-wrap">
        <select
          value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)}
          data-testid="users-role-filter"
          className="border border-slate-200 rounded px-2 py-1.5 text-sm"
        >
          <option value="all">All roles</option>
          {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
        </select>
        <select
          value={activeFilter} onChange={(e) => setActiveFilter(e.target.value)}
          data-testid="users-active-filter"
          className="border border-slate-200 rounded px-2 py-1.5 text-sm"
        >
          <option value="all">Active & inactive</option>
          <option value="active">Active only</option>
          <option value="inactive">Inactive only</option>
        </select>
        <span className="text-xs text-slate-500">{filtered.length} user(s)</span>
        <button
          type="button" onClick={() => setShowCreate(true)}
          data-testid="users-create-btn"
          className="ml-auto inline-flex items-center gap-1 bg-slate-900 text-white text-sm px-3 py-1.5 rounded hover:bg-slate-800"
        >
          <UserPlus className="w-4 h-4" /> New user
        </button>
      </div>

      {error && <ErrorBlock error={error} />}
      {loading ? <LoadingBlock /> : (
        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase text-slate-500">
              <tr>
                <th className="text-left px-3 py-2">Email</th>
                <th className="text-left px-3 py-2">Name</th>
                <th className="text-left px-3 py-2">Role</th>
                <th className="text-left px-3 py-2">Status</th>
                <th className="text-left px-3 py-2">Last login</th>
                <th className="text-right px-3 py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((u) => (
                <tr key={u.id} data-testid={`user-row-${u.id}`} className="border-t border-slate-100">
                  <td className="px-3 py-2 font-mono text-xs">{u.email}</td>
                  <td className="px-3 py-2">{u.name || `${u.first_name || ""} ${u.last_name || ""}`.trim() || "—"}</td>
                  <td className="px-3 py-2">
                    <select
                      value={u.role} onChange={(e) => patch(u.id, { role: e.target.value }, "Role updated")}
                      data-testid={`user-role-${u.id}`}
                      className="border border-slate-200 rounded px-1 py-0.5 text-xs"
                    >
                      {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
                    </select>
                  </td>
                  <td className="px-3 py-2">
                    <span className={`text-xs px-2 py-0.5 rounded border ${u.is_active !== false ? "bg-emerald-100 text-emerald-700 border-emerald-200" : "bg-slate-100 text-slate-500 border-slate-200"}`}>
                      {u.is_active !== false ? "active" : "inactive"}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-xs text-slate-500">
                    {u.last_login ? u.last_login.slice(0, 19).replace("T", " ") : "—"}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <button
                      onClick={() => patch(u.id, { is_active: !(u.is_active !== false) }, "Status updated")}
                      data-testid={`user-toggle-${u.id}`}
                      className="text-xs px-2 py-1 text-slate-600 hover:bg-slate-100 rounded inline-flex items-center gap-1"
                      title={u.is_active !== false ? "Deactivate" : "Activate"}
                    >
                      <Ban className="w-3 h-3" /> {u.is_active !== false ? "Deactivate" : "Activate"}
                    </button>
                    <button
                      onClick={() => resetPassword(u)}
                      data-testid={`user-reset-${u.id}`}
                      className="text-xs px-2 py-1 text-slate-600 hover:bg-slate-100 rounded inline-flex items-center gap-1"
                    >
                      <KeyRound className="w-3 h-3" /> Reset password
                    </button>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && !loading && (
                <tr><td colSpan={6} className="px-3 py-6 text-center text-slate-500">No users match these filters.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {showCreate && (
        <CreateUserDialog
          tenantId={tenantId}
          onClose={() => setShowCreate(false)}
          onCreated={() => { setShowCreate(false); load(); }}
        />
      )}
    </div>
  );
}

function CreateUserDialog({ tenantId, onClose, onCreated }) {
  const [form, setForm] = useState({ email: "", name: "", role: "admin" });
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    setSaving(true);
    try {
      const res = await axios.post(`${API}/super-admin/users`, {
        tenant_id: tenantId, email: form.email, name: form.name, role: form.role,
      }, { headers: authHeader() });
      const temp = res.data?.temp_password;
      if (temp) {
        window.prompt("Temporary password (copy now — shown once):", temp);
      }
      toast.success("User created");
      onCreated();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Create failed");
    } finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="users-create-dialog">
      <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md space-y-3">
        <h3 className="font-semibold text-slate-800">New tenant user</h3>
        <input
          type="email" placeholder="Email"
          value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
          data-testid="create-user-email"
          className="w-full border border-slate-200 rounded px-2 py-1.5 text-sm"
        />
        <input
          type="text" placeholder="Name (optional)"
          value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
          data-testid="create-user-name"
          className="w-full border border-slate-200 rounded px-2 py-1.5 text-sm"
        />
        <select
          value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}
          data-testid="create-user-role"
          className="w-full border border-slate-200 rounded px-2 py-1.5 text-sm"
        >
          {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
        </select>
        <div className="flex justify-end gap-2 pt-2">
          <button onClick={onClose} className="px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-100 rounded">Cancel</button>
          <button
            onClick={submit} disabled={!form.email || saving}
            data-testid="create-user-submit"
            className="px-3 py-1.5 text-sm bg-slate-900 text-white rounded disabled:opacity-40 inline-flex items-center gap-1"
          >
            {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <UserPlus className="w-3 h-3" />}
            Create
          </button>
        </div>
      </div>
    </div>
  );
}


// ============================================================================
// Integrations tab — safe-identifier only, with connection tests
// ============================================================================

// iter341 — Super-Admin managed per-tenant email sending domain (Resend).
function EmailSendingDomainCard({ tenantId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ domain: "", from_name: "", from_address: "", reply_to: "" });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/super-admin/tenants/${tenantId}/email-identity`, { headers: authHeader() });
      setData(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load email domain");
    } finally { setLoading(false); }
  }, [tenantId]);
  useEffect(() => { load(); }, [load]);

  const register = async () => {
    if (!form.domain || !form.from_address || !form.reply_to) {
      toast.error("Domain, From address and Reply-to are required"); return;
    }
    setBusy(true);
    try {
      await axios.post(`${API}/super-admin/tenants/${tenantId}/email-identity`, form, { headers: authHeader() });
      toast.success("Domain registered — publish the DNS records below, then Verify");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Registration failed"); }
    finally { setBusy(false); }
  };

  const verify = async () => {
    setBusy(true);
    try {
      const res = await axios.post(`${API}/super-admin/tenants/${tenantId}/email-identity/verify`, {}, { headers: authHeader() });
      const st = res.data.domain_status;
      if (st === "verified") toast.success("Verified — this tenant now sends from its own address");
      else toast.message(`Status: ${st}. DNS can take time to propagate; try Verify again shortly.`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Verification failed"); }
    finally { setBusy(false); }
  };

  const remove = async () => {
    if (!window.confirm("Remove this sending domain? Emails revert to the platform default From.")) return;
    setBusy(true);
    try {
      await axios.delete(`${API}/super-admin/tenants/${tenantId}/email-identity`, { headers: authHeader() });
      toast.success("Reverted to platform default sender");
      setData(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Remove failed"); }
    finally { setBusy(false); }
  };

  const status = data?.domain_status;
  const statusCls = status === "verified" ? "bg-emerald-100 text-emerald-700 border-emerald-200"
    : status === "failed" ? "bg-rose-100 text-rose-700 border-rose-200"
    : status ? "bg-amber-100 text-amber-800 border-amber-200"
    : "bg-slate-100 text-slate-600 border-slate-200";

  return (
    <Card title={<span className="inline-flex items-center gap-2"><Mail className="w-4 h-4 text-slate-500" />Email Sending Domain</span>} testid="sa-email-identity-card">
      <p className="text-xs text-slate-500 mb-3">
        Register this tenant&apos;s own domain so their emails send from their address. Until a domain is verified, mail falls back to the platform default
        {data?.platform_default_from ? ` (${data.platform_default_from})` : ""}.
      </p>
      {loading ? <LoadingBlock /> : !data?.configured ? (
        <div className="space-y-2" data-testid="sa-email-identity-form">
          {[["domain", "Sending domain — e.g. mail.academy.com"],
            ["from_name", "From name — e.g. Sparc Cricket Academy"],
            ["from_address", "From address — e.g. no-reply@mail.academy.com"],
            ["reply_to", "Reply-to address"]].map(([k, ph]) => (
            <input key={k} value={form[k]} onChange={(e) => setForm((f) => ({ ...f, [k]: e.target.value }))}
              placeholder={ph} data-testid={`sa-email-identity-${k}`}
              className="w-full border border-slate-200 rounded px-3 py-2 text-sm" />
          ))}
          <button type="button" disabled={busy} onClick={register} data-testid="sa-email-identity-register"
            className="text-xs px-3 py-1.5 bg-slate-800 text-white rounded inline-flex items-center gap-1 disabled:opacity-40">
            {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />} Register domain
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`text-xs px-2 py-0.5 rounded border ${statusCls}`} data-testid="sa-email-identity-status">{status || "unknown"}</span>
            <span className="text-xs text-slate-500 font-mono">{data.from_name ? `${data.from_name} ` : ""}&lt;{data.from_address}&gt;</span>
          </div>
          <div
            className={`rounded-md border px-3 py-2 text-xs ${data.override_active ? "bg-emerald-50 border-emerald-200 text-emerald-800" : "bg-amber-50 border-amber-200 text-amber-800"}`}
            data-testid="sa-email-identity-effective"
          >
            <div className="font-medium">
              {data.override_active ? "Customer email is sent as:" : "⚠ Customer email is NOT using this domain yet. It is sent as:"}
            </div>
            <div className="font-mono mt-0.5 break-all" data-testid="sa-email-identity-effective-from">{data.effective_from}</div>
            {!data.override_active && data.reason && (
              <div className="mt-1" data-testid="sa-email-identity-reason">{data.reason}</div>
            )}
          </div>
          <Row label="Domain" value={data.domain_name} mono tid="sa-email-identity-domain" />
          <Row label="Reply-to" value={data.reply_to} mono tid="sa-email-identity-replyto" />
          {(data.records || []).length > 0 && (
            <div data-testid="sa-email-identity-records">
              <div className="text-xs font-medium text-slate-600 mb-1">Publish these DNS records at the tenant&apos;s registrar, then click Verify:</div>
              <div className="overflow-x-auto">
                <table className="w-full text-[11px] border border-slate-200">
                  <thead className="bg-slate-50 text-slate-500"><tr>
                    <th className="text-left px-2 py-1">Type</th><th className="text-left px-2 py-1">Name</th>
                    <th className="text-left px-2 py-1">Value</th><th className="text-left px-2 py-1">Status</th>
                  </tr></thead>
                  <tbody>
                    {data.records.map((r, idx) => (
                      <tr key={idx} className="border-t border-slate-100 font-mono">
                        <td className="px-2 py-1">{r.type || r.record}</td>
                        <td className="px-2 py-1 break-all">{r.name}</td>
                        <td className="px-2 py-1 break-all">{r.value}</td>
                        <td className="px-2 py-1">{r.status || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          <div className="flex gap-2">
            <button type="button" disabled={busy} onClick={verify} data-testid="sa-email-identity-verify"
              className="text-xs px-3 py-1.5 border border-slate-200 rounded inline-flex items-center gap-1 hover:bg-slate-50 disabled:opacity-40">
              {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <ShieldCheck className="w-3 h-3" />} Verify
            </button>
            <button type="button" disabled={busy} onClick={remove} data-testid="sa-email-identity-remove"
              className="text-xs px-3 py-1.5 border border-rose-200 text-rose-600 rounded inline-flex items-center gap-1 hover:bg-rose-50 disabled:opacity-40">
              <Ban className="w-3 h-3" /> Remove
            </button>
          </div>
        </div>
      )}
    </Card>
  );
}


export function TenantIntegrationsTab({ tenantId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [testing, setTesting] = useState(null);
  const [testResults, setTestResults] = useState({});

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res = await axios.get(`${API}/super-admin/tenants/${tenantId}/integrations`, { headers: authHeader() });
      setData(res.data);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally { setLoading(false); }
  }, [tenantId]);
  useEffect(() => { load(); }, [load]);

  const test = async (key) => {
    setTesting(key);
    try {
      const res = await axios.post(
        `${API}/super-admin/tenants/${tenantId}/integrations/${key}/test`,
        {}, { headers: authHeader() },
      );
      setTestResults((r) => ({ ...r, [key]: res.data }));
      if (res.data.ok) toast.success(`${key}: OK`);
      else toast.error(`${key}: ${res.data.reason || "failed"}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Test failed");
    } finally {
      setTesting(null);
      load();
    }
  };

  if (loading) return <LoadingBlock />;
  if (error) return <ErrorBlock error={error} />;
  if (!data) return null;

  const testable = new Set(["stripe", "resend", "cloudflare"]);

  return (
    <div className="space-y-4" data-testid="tenant-tab-integrations">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {(data.integrations || []).map((i) => {
        const result = testResults[i.key];
        return (
          <Card key={i.key} title={<span className="inline-flex items-center gap-2"><Zap className="w-4 h-4 text-slate-500" />{i.name}</span>} testid={`integ-card-${i.key}`}>
            <div className="flex items-center gap-2 mb-3 flex-wrap">
              <span className={`text-xs px-2 py-0.5 rounded border ${i.configured ? "bg-emerald-100 text-emerald-700 border-emerald-200" : "bg-slate-100 text-slate-500 border-slate-200"}`}
                    data-testid={`integ-status-${i.key}`}>
                {i.configured ? (i.mode ? `${i.mode} mode` : "configured") : "not configured"}
              </span>
              <span className="text-[10px] text-slate-500">owner: {i.owner}</span>
              {i.last_tested_at && (
                <span className="text-[10px] text-slate-400">
                  tested {i.last_tested_at.slice(0, 19).replace("T", " ")} · {i.last_ok ? "OK" : "FAIL"}
                </span>
              )}
            </div>
            {i.publishable_key_hint && <Row label="Publishable key" value={i.publishable_key_hint} mono tid={`integ-pk-${i.key}`} />}
            {i.zone_id && <Row label="Zone ID" value={i.zone_id} mono tid={`integ-zone-${i.key}`} />}
            {i.account_id && <Row label="Account ID" value={i.account_id} mono tid={`integ-acc-${i.key}`} />}
            {result && (
              <div className="mt-3 bg-slate-50 border border-slate-200 rounded p-2 text-xs" data-testid={`integ-result-${i.key}`}>
                <div className="flex items-center gap-1 font-medium mb-1">
                  {result.ok
                    ? <CheckCircle2 className="w-3 h-3 text-emerald-600" />
                    : <AlertTriangle className="w-3 h-3 text-amber-600" />}
                  {result.ok ? "OK" : (result.reason || "Failed")}
                </div>
                {result.account_email && <div className="text-slate-500 font-mono">{result.account_email}</div>}
                {result.domain_count != null && <div className="text-slate-500">{result.domain_count} domain(s)</div>}
              </div>
            )}
            <button
              type="button"
              disabled={!i.configured || !testable.has(i.key) || testing === i.key}
              onClick={() => test(i.key)}
              data-testid={`integ-test-${i.key}`}
              className="mt-3 text-xs px-3 py-1.5 border border-slate-200 rounded inline-flex items-center gap-1 hover:bg-slate-50 disabled:opacity-40"
            >
              {testing === i.key ? <Loader2 className="w-3 h-3 animate-spin" /> : <ShieldCheck className="w-3 h-3" />}
              Test connection
            </button>
          </Card>
        );
      })}
      </div>
      <TenantStorageCard tenantId={tenantId} />
      <EmailSendingDomainCard tenantId={tenantId} />
    </div>
  );
}
